import lodash from 'lodash'
import HTTP from '@/utils/http'
import notification from '@/utils/notification'
import i18n from '@/utils/i18n'
import store from '@/store'
import { ADDENDUM_STATUS, ADDENDUM_PENDING_STATUS, ActionTypes } from '@/utils/consts'
import { tenderStatus, vendorBidStatus } from '@/utils/locale'
import { validateSite } from '@/api/departments/request'
import { getDepartmentByLocationCode } from '@/api/locations/request'
import { save } from '@/utils/xfs'

export function isTenderInAddendum (tender = {}, activeStatusOnly = false) {
  return tender.version && (tender.status in (activeStatusOnly ? ADDENDUM_PENDING_STATUS : ADDENDUM_STATUS))
}

export function parseStatusText (tenderGeneral) {
  if (tenderGeneral && tenderGeneral.status) {
    if (isTenderInAddendum(tenderGeneral)) {
      return `A-${tenderGeneral.status}`
    }
    return tenderGeneral.status
  }
  return 'DRAFT'
}

/**
 * Get tenders
 */
export async function getTenders () {
  const response = await HTTP.get('tenders')
  const tenders = deep(response, 'data.data')
  return tenders
}

export async function getReviewList () {
  const response = await HTTP.get('tenders/as-representative')
  const tenders = deep(response, 'data.data')
  return tenders
}

export async function getTender (tenderId) {
  try {
    const response = await HTTP.get(`tenders/${tenderId}`)
    const tender = response.data.data
    return tender
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function getTermIdByTenderId (id) {
  try {
    const response = await HTTP.get(`/tenders/${id}/termsId`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function getTenderAccounts (tenderId) {
  try {
    const response = await HTTP.get(`tenders/${tenderId}/accounts`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function updateTenderAccounts (tenderId, request) {
  try {
    const response = await HTTP.put(`tenders/${tenderId}/accounts`, request)
    return response.data.data
  } catch (err) {
    notification.handleServerErrorMessage(err)
    throw err
  }
}

export async function updateGeneralFields (tenderId, general = {}) {
  try {
    let request = {}
    request.general = general
    await HTTP.put(`tenders/${tenderId}`, request)
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function listAllTenders (query, diskCacheKey) {
  try {
    const response = await HTTP
      .silent(false)
      .diskCache(diskCacheKey)
      .get('tenders/all', { params: query })
    const result = response.data
    return [result.data, result.count]
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function exportAllTenders (query) {
  const response = await HTTP.get('/tenders/all/export', { params: query, responseType: 'arraybuffer' })
  save(response)
}

export async function updateTender (request = {}, tenderId, showNotification = true, updatedBidStatus = false) {
  try {
    let requestData = JSON.parse(JSON.stringify(request))

    // to exclude vendor bid data when update tender <Michelle>
    const bids = requestData.bid || []
    bids.forEach(bid => {
      if (!updatedBidStatus) {
        delete bid.status
      } else if (bid.status !== vendorBidStatus.DECLINED) {
        bid.status = vendorBidStatus.INVITED
      }
      delete bid.content
      delete bid.version
      delete bid.respondedOn
    })

    requestData = lodash.omit(requestData, ['general.managedBy', 'general.manager'])

    const headers = {}
    if (request.auditLogAction) {
      headers['X-CMS-Action'] = request.auditLogAction
    }
    if (requestData.general.conditionsOfContracts) {
      const path = store.getters['tenders/cocI18nPath']
      const type = lodash.get(requestData, 'general.conditionsOfContracts', '').toLowerCase()
      const value = i18n.t(path)[0][type]
      requestData.deleteDocs = !Array.isArray(value)
    }
    const response = await HTTP.put(`/tenders/${tenderId}`, requestData, { headers })
    if (showNotification) {
      notification.handleResponseAlert(response)
    }
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function updateTenderStatus (request, tenderId, action) {
  const isTender = store.getters['tenders/isTender']
  const isTenderFirstSubmit = request.status === tenderStatus.WAPPR && action === ActionTypes.tenders.submit
  const isITQFirstIssue = request.status === tenderStatus.ISSUED && action === ActionTypes.tenders.issue
  const toLockTermsVersion = isTender ? isTenderFirstSubmit : isITQFirstIssue

  if (toLockTermsVersion) {
    const maxVersion = store.getters['tenders/maxTermVersion']
    request.termsVersion = maxVersion
  }
  const response = await HTTP.patch(`/tenders/${tenderId}`, request, { headers: { 'X-CMS-Action': action } })
  notification.handleResponseAlert(response)
  return true
}

export async function updateAnalysisReco (request, tenderId) {
  try {
    await HTTP.put(`/tenders/${tenderId}/reco`, { data: request.data })
  } catch (err) {
    notification.handleResponseError(err, 'reco')
    throw err
  }
}

export async function validatePR (tenderId, prNumber, tenderType, budgetBase, site) {
  const isSiteValid = await validateSite(site)
  if (!isSiteValid) {
    notification.fireNotification(false, i18n.t('tender.general.siteCodeError'))
    return false
  }
  const dept = await getDepartmentByLocationCode(site)
  if (!dept) {
    notification.fireNotification(false, i18n.t('tender.general.siteNotAssignDept'))
    return false
  }

  const isPRAmountValid = validatePRAmount(dept, budgetBase, tenderType)
  if (!isPRAmountValid) {
    notification.fireNotification(false, i18n.t('tender.general.thresholdError', [tenderType]))
    return false
  }

  const isPRNoValid = await validatePRNumber(tenderId, prNumber)
  if (!isPRNoValid) {
    notification.fireNotification(false,
      i18n.t('tender.general.duplicatePRError'))
    return false
  }
  return true
}

export function validatePRAmount (dept, budgetBase, tenderType) {
  const NumberBudget = Number(budgetBase)
  if (NumberBudget) {
    // Budget from maximo is string, threshold from db is string, should transfer to number to compare.
    const threshold = Number(dept.tenderAmountThreshold)
    if (!threshold) {
      return false
    }

    return tenderType === 'TENDER'
      ? NumberBudget > threshold
      : NumberBudget <= threshold
  }
  return false
}

export async function validatePRNumber (tenderId, prNumber) {
  if (prNumber) {
    const bannedStatus = tenderStatus.CANCELLED
    const tendersData = await getResponseData(
      `/tenders/count?^id=${tenderId}&^status=${bannedStatus}&prNumber=${prNumber}`)
    return tendersData <= 0
  }
  return false
}

async function getResponseData (url) {
  let response = null
  try {
    response = await HTTP.get(url)
  } catch (err) {
    notification.handleResponseError(err)
  }

  return response.data.data
}

export async function getAwaredTenderQuantityByPO (poNumber) {
  const status = tenderStatus.AWARDED
  const awaredTenderQuantity = await getResponseData(
    `/tenders/count?status=${status}&poNumber=${poNumber}`)
  return awaredTenderQuantity
}

export async function checkPOAwarded (poNumber) {
  try {
    const response = await HTTP.get(`/purchases/orders/${poNumber}/is-awarded`)
    return (response && response.data) ? response.data.data : false
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function checkContractAwarded (contractNumber, contractRevision) {
  try {
    const query = {
      revisionNum: contractRevision,
    }
    const response = await HTTP.get(`/purchases/contracts/${contractNumber}/is-awarded`, { params: query })
    return (response && response.data) ? response.data.data : false
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function checkSiteValidForContract (siteId, contractNumber, contractRevision) {
  try {
    const query = {
      siteId,
      revisionNum: contractRevision,
    }
    const response = await HTTP.get(`/purchases/contracts/${contractNumber}/is-site-valid`, { params: query })
    return (response && response.data) ? response.data.data : false
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function bindNumber (tenderId, resourceMap, type) {
  try {
    const response = await HTTP.post(`/tenders/${tenderId}/bind`, {
      resourceMap,
      type,
    })
    return (response && response.data) ? response.data.data : []
  } catch (err) {
    notification.handleResponseError(err)
  }
}

export async function inviteRepresentative (tenderId, request) {
  try {
    const response = await HTTP.post(`/tenders/${tenderId}/invite`, request)
    notification.handleResponseAlert(response)
    return response.data.data || []
  } catch (err) {
    notification.handleResponseError(err)
    return null
  }
}

export async function checkEditable (tenderId, userInfo) {
  try {
    const response = await HTTP.get(`/tenders/${tenderId}/check-editable`, { params: userInfo })
    return response.data || {}
  } catch (err) {
    notification.handleResponseError(err)
    return null
  }
}

export async function keepEdit (tenderId, userInfo) {
  try {
    const response = await HTTP.silentPut(`/tenders/${tenderId}/keep-edit`, userInfo)
    return response.data || {}
  } catch (err) {
    notification.handleResponseError(err)
    return null
  }
}
